import React, { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';
import { useAuth } from '../contexts/AuthContext';

const InstallPrompt = () => {
  const { user } = useAuth();
  const { 
    canInstall, 
    isIOS,
    isIOSSafari, 
    showInstallPrompt, 
    showIOSInstructions,
    hideIOSInstructions,
    showIOSInstallPrompt,
    isMobile,
    isInstalled
  } = usePWA();
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Only show prompt after user is logged in and on mobile
    if (user && isMobile && !isInstalled && canInstall && !isDismissed) {
      // Delay showing the prompt to not interrupt the login flow
      const timer = setTimeout(() => {
        console.log('PWA: Showing install prompt');
        setShowPrompt(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [user, isMobile, isInstalled, canInstall, isDismissed, isIOS, isIOSSafari]);

  const handleInstall = async () => {
    if (isIOSSafari) {
      // Only show manual instructions for Safari on iOS
      showIOSInstructions();
    } else if (isIOS && !isIOSSafari) {
      // Chrome/Firefox/Edge on iOS - show Chrome-specific instructions
      showIOSInstructions();
    } else {
      // For Android Chrome, etc. - use native prompt
      const installed = await showInstallPrompt();
      if (installed) {
        setShowPrompt(false);
      } else {
        // If native prompt failed, show manual instructions as fallback
        console.log('PWA: Native prompt failed, showing manual instructions');
        showIOSInstructions();
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setIsDismissed(true);
    // Remember dismissal for this session
    sessionStorage.setItem('install-prompt-dismissed', 'true');
  };

  // Don't show if already dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('install-prompt-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Don't render anything if user is not logged in, not on mobile, or already installed
  if (!user || !isMobile || isInstalled) {
    return null;
  }

  return (
    <>
      {/* Main Install Prompt - Simplified */}
      {showPrompt && (
        <div className="fixed top-4 left-4 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 transform transition-all duration-300 ease-in-out">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-sky-600 rounded-lg flex items-center justify-center">
                <svg 
                  className="w-6 h-6 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 18h.01M6 6h12l-1 9H7L6 6z M6 6V4a2 2 0 012-2h8a2 2 0 012 2v2" 
                  />
                </svg>
              </div>
            </div>
            
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                Add to Home Screen
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Stay logged in with {isIOS ? 'Face ID/Touch ID' : 'biometric authentication'} and get faster access.
              </p>
              
              <div className="flex space-x-2">
                <button
                  onClick={handleInstall}
                  className="flex-1 bg-sky-600 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-sky-700 transition-colors"
                >
                  Add to Home Screen
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-3 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Later
                </button>
              </div>
            </div>
            
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* iOS Install Instructions Modal - For Safari and Chrome on iOS */}
      {showIOSInstallPrompt && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-sm w-full p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-sky-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8l-8-8-8 8" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add to Home Screen
              </h3>
              
              <div className="text-center space-y-4 mb-6">
                <div className="bg-sky-50 rounded-lg p-4">
                  <div className="text-sm text-gray-700 mb-2">
                    <strong>Tap the {isIOSSafari ? 'Share' : 'Menu'} button</strong> 
                    {isIOSSafari ? (
                      <svg className="inline w-5 h-5 mx-1 text-sky-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                      </svg>
                    ) : (
                      <span className="inline-block w-5 h-5 mx-1 text-sky-600">⋮</span>
                    )}
                    <br />
                    <span className="text-xs text-gray-600">
                      {isIOSSafari ? 'at the bottom of Safari' : 'in the top-right corner of Chrome'}
                    </span>
                  </div>
                </div>
                
                <div className="text-2xl">⬇️</div>
                
                <div className="bg-sky-50 rounded-lg p-4">
                  <div className="text-sm text-gray-700">
                    <strong>Select "Add to Home Screen"</strong>
                    <br />
                    <span className="text-xs text-gray-600">then tap "Add"</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-green-800">
                  <strong>✨ Benefits:</strong> Stay logged in with Face ID/Touch ID and faster access!
                </p>
              </div>
              
              <button
                onClick={hideIOSInstructions}
                className="w-full bg-sky-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-sky-700 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt; 